import { FlyToInterpolator, WebMercatorViewport } from '@deck.gl/core';
import { atom } from 'jotai';
import type { BBox } from 'geojson';
import type { MapViewState } from './deckgl-types';
import { clickInfoAtom, viewStateAtom } from './atoms';

/** Pixels of breathing room left around the fitted bounds. */
const FIT_BOUNDS_PADDING = 20;
const FLY_TO_SPEED = 3.0;
const FLY_TO_DURATION_MS = 1000;

/** The camera, with the canvas size Map writes onto it from deck's resize events. */
type Camera = MapViewState & { width?: number; height?: number };

type CameraUpdate = (update: (viewState: Camera) => Camera) => void;

/**
 * Flies the camera to frame the bounds inside the map canvas, viewed top-down.
 *
 * The fit is computed against the canvas size the camera carries, so the same
 * bounds fill a wide monitor and a narrow sidebar-inset canvas alike. Fitting
 * against any other size makes the zoom depend on how far the real canvas
 * differs from it: clipped on a smaller canvas, lost in the middle of a larger
 * one.
 *
 * The camera lacks a size only before deck's first measure, when no map is on
 * screen to click, so an unsized camera here is a bug and throws. Callers that
 * frame data as it arrives wait on cameraSizedAtom instead of racing that
 * first measure.
 */
export function flyToBounds(
  bounds: BBox,
  setViewState: CameraUpdate,
  padding: number = FIT_BOUNDS_PADDING
): void {
  const [minLng, minLat, maxLng, maxLat] = bounds;
  setViewState(viewState => {
    if (viewState.width == null || viewState.height == null) {
      throw new Error('The camera carries no canvas size to fit the bounds into');
    }
    const fitted = new WebMercatorViewport({
      width: viewState.width,
      height: viewState.height,
    }).fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding }
    );
    return {
      ...viewState,
      longitude: fitted.longitude,
      latitude: fitted.latitude,
      zoom: fitted.zoom,
      pitch: 0,
      bearing: 0,
      transitionInterpolator: new FlyToInterpolator({ speed: FLY_TO_SPEED }),
      transitionDuration: FLY_TO_DURATION_MS,
    };
  });
}

export type CameraFlight = (
  | { bounds: BBox; padding?: number }
  | { center: [longitude: number, latitude: number]; zoom: number }
) & {
  /**
   * Keep the feature popup open through the flight. A flight closes it by
   * default, since it moves the map out from under the feature the popup
   * describes; a flight to that same feature has no reason to.
   */
  keepPopup?: boolean;
};

/**
 * A camera flight started away from the map -- a list row, a chart point, a
 * region, an incident arrival. Closes the feature popup unless told to keep
 * it, then frames the bounds, or flies to the centre and zoom, top-down.
 */
export const cameraFlightAtom = atom(null, (_get, set, flight: CameraFlight) => {
  if ('bounds' in flight) {
    flyToBounds(flight.bounds, update => set(viewStateAtom, update), flight.padding);
  } else {
    const [longitude, latitude] = flight.center;
    set(viewStateAtom, viewState => ({
      ...viewState,
      longitude,
      latitude,
      zoom: flight.zoom,
      pitch: 0,
      bearing: 0,
      transitionInterpolator: new FlyToInterpolator({ speed: FLY_TO_SPEED }),
      transitionDuration: FLY_TO_DURATION_MS,
    }));
  }
  // After the camera write, so a flight that throws -- an unsized camera,
  // empty bounds -- leaves the popup as it was.
  if (!flight.keepPopup) {
    set(clickInfoAtom, null);
  }
});
